import { Injectable } from '@angular/core';
import { UrlStateConfig, UrlStateItem } from '../interfaces/url-routing.model';

@Injectable({
  providedIn: 'root',
})
export class UrlStateSerializerService<TValue = unknown> {
  private getDefaultConfig(): Required<UrlStateConfig<TValue>> {
    return {
      queryParamPrefix: '',
      fieldAbbreviations: {},
      operatorAbbreviations: {
        equals: 'eq',
        greaterThan: 'gt',
        lessThan: 'lt',
        between: 'bt',
        startsWith: 'sw',
        contains: 'ct',
        greaterThanOrEqual: 'gtoe',
        lessThanOrEqual: 'ltoe',
        oneOf: 'in',
        is: 'is',
        isNot: 'not',
      },
      serializer: {
        serialize: (item: UrlStateItem<TValue>) => JSON.stringify(item),
        deserialize: (value: string) => JSON.parse(value),
      },
    };
  }

  /**
   * Serialize multiple filter groups to query params
   * Each group uses its own config and prefix
   */
  public serializeMultipleToQueryParams(
    groups: Array<{
      items: UrlStateItem<TValue>[];
      config: Partial<UrlStateConfig<TValue>>;
    }>
  ): { [key: string]: string | string[] } {
    const result: { [key: string]: string | string[] } = {};

    groups.forEach(({ items, config }) => {
      const serialized = this.serializeToQueryParams(items, config);
      Object.assign(result, serialized);
    });

    return result;
  }

  /**
   * Deserialize multiple filter groups from query params
   */
  public deserializeMultipleFromQueryParams(
    params: Record<string, unknown>,
    configs: Array<{ prefix: string; config: Partial<UrlStateConfig<TValue>> }>
  ): Array<{
    prefix: string;
    items: UrlStateItem<TValue>[];
  }> {
    return configs.map(({ prefix, config }) => {
      const relevantParams = { [prefix]: params[prefix] };
      const items = this.deserializeFromQueryParams(relevantParams, {
        ...config,
        queryParamPrefix: prefix,
      });
      return { prefix, items };
    });
  }

  public serializeToQueryParams(
    items: UrlStateItem<TValue>[],
    config?: Partial<UrlStateConfig<TValue>>
  ): { [key: string]: string | string[] } {
    const mergedConfig = { ...this.getDefaultConfig(), ...config };

    if (!items.length) {
      return {};
    }

    const stateParamValues = items.map((item) =>
      this.serializeItem(item, mergedConfig)
    );

    return {
      [mergedConfig.queryParamPrefix]: stateParamValues,
    };
  }

  public deserializeFromQueryParams(
    params: Record<string, unknown>,
    config?: Partial<UrlStateConfig<TValue>>
  ): UrlStateItem<TValue>[] {
    const mergedConfig = { ...this.getDefaultConfig(), ...config };
    const reverseFieldAbbr = this.reverseMap(mergedConfig.fieldAbbreviations);
    const reverseOperatorAbbr = this.reverseMap(
      mergedConfig.operatorAbbreviations
    );

    const rawValues = params[mergedConfig.queryParamPrefix];
    if (!rawValues) return [];

    const values: string[] = Array.isArray(rawValues) ? rawValues : [rawValues];

    return values
      .map((entry) =>
        this.deserializeItem(entry, reverseFieldAbbr, reverseOperatorAbbr)
      )
      .filter(Boolean) as UrlStateItem<TValue>[];
  }

  private serializeItem(
    item: UrlStateItem<TValue>,
    config: Required<UrlStateConfig<TValue>>
  ): string {
    const fieldKey = config.fieldAbbreviations[item.field] || item.field;
    const operatorKey =
      config.operatorAbbreviations[item.operator] || item.operator;

    const serializedValue = this.serializeValue(item);
    return `${fieldKey}:${operatorKey}:${serializedValue}`;
  }

  private serializeValue(item: UrlStateItem<TValue>): string {
    if (item.value2 !== undefined) {
      return `${item.value},${item.value2}`;
    }

    try {
      const parsed =
        typeof item.value === 'string'
          ? JSON.parse(item.value as string)
          : item.value;
      if (
        parsed?.option === 'betweenDateTime' &&
        parsed.value?.start &&
        parsed.value?.end
      ) {
        return `${parsed.value.start},${parsed.value.end}`;
      }
      return parsed.value || String(item.value);
    } catch {
      return String(item.value);
    }
  }

  private deserializeItem(
    entry: string,
    reverseFieldAbbr: { [key: string]: string },
    reverseOperatorAbbr: { [key: string]: string }
  ): UrlStateItem<TValue> | null {
    if (!entry) return null;

    const [fieldAbbr, operatorAbbr, ...rest] = entry.split(':');
    const value = rest.join(':');

    const field = reverseFieldAbbr[fieldAbbr] || fieldAbbr;
    const operator = reverseOperatorAbbr[operatorAbbr] || operatorAbbr;

    if ((operator === 'bt' || operator === 'between') && value.includes(',')) {
      const [value1, value2] = value.split(',');
      return {
        field,
        operator: operator as UrlStateItem<TValue>['operator'],
        value: value1 as TValue,
        value2: value2 as TValue,
      };
    }

    return {
      field,
      operator: operator as UrlStateItem<TValue>['operator'],
      value: value as TValue,
    };
  }

  private reverseMap(map: { [key: string]: string }): {
    [key: string]: string;
  } {
    return Object.fromEntries(Object.entries(map).map(([k, v]) => [v, k]));
  }
}
